import BlockApi from '@/api/api';
import NoMetadataPlaceholder from '@/components/NoMetadataPlaceholder';
import { MetadataContext } from '@/context/MetadataContext';
import { useConfig } from '@/hooks/useConfig';
import { useDialogs } from '@/hooks/useDialogs';
import { Metadata, MetadataInfo } from '@/types/metadata/metadata';
import { emptyMetadata, emptyMetadataInfo } from '@/util/metadata';
import { Box, CircularProgress } from '@mui/material';
import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocalStorage } from 'usehooks-ts';

export const MetadataProvider = ({ children }: { children: React.ReactNode }) => {
  const [metadata, setMetadata] = useLocalStorage<Metadata>('metadata', emptyMetadata);
  const [metadataInfo, setMetadataInfo] = useLocalStorage<MetadataInfo>('metadataInfo', emptyMetadataInfo);

  const { config } = useConfig();
  const { confirm } = useDialogs();
  const auth = useAuth();

  const loadMetadataInfo = async () => {
    if (!auth.user || !auth.isAuthenticated) return;
    BlockApi(config.apiUrl, auth.user.access_token)
      .configuration.metadataInfo()
      .then((newMetadata) => {
        console.debug('new Metadata: ', newMetadata);
        if (!newMetadata) return;

        if (!metadataInfo?.meta.generated || metadataInfo?.meta.generated < newMetadata.meta.generated) {
          confirm({
            title: 'There is a newer metadata file available.',
            description: newMetadata.meta.description,
            confirmationText: 'Load new Metadata',
            cancellationButtonProps: { color: 'secondary' }
          }).then((result) => {
            if (!result.confirmed) return;
            loadMetadata();
          });
        }
      });
  };

  const loadMetadata = async () => {
    if (!auth.user || !auth.isAuthenticated) return;
    BlockApi(config.apiUrl, auth.user.access_token)
      .configuration.metadata()
      .then((newMetadata) => {
        console.debug('new Metadata: ', newMetadata);
        if (
          newMetadata &&
          (!metadataInfo?.meta.generated ||
            new Date(newMetadata.meta.generated) > new Date(metadataInfo.meta.generated))
        ) {
          const { data, ...info } = newMetadata;
          setMetadata(data);
          setMetadataInfo(info);
        }
      });
  };

  useEffect(() => {
    auth.isAuthenticated && loadMetadataInfo();

    //For testing purposes only
    //setMetadata(metaJson)
  }, [auth.isAuthenticated]);

  if (!metadata) {
    if (!auth.isLoading) return <NoMetadataPlaceholder updateMetadata={() => loadMetadata()} />;
    else
      return (
        <Box textAlign={'center'} mt={15}>
          <CircularProgress color='inherit' />
        </Box>
      );
  }
  return (
    <MetadataContext.Provider
      value={{
        metadata: metadata,
        metadataInfo: metadataInfo
      }}
    >
      {children}
    </MetadataContext.Provider>
  );
};
